import type { ReactNode } from 'react';
import BadgeRating from 'components/BadgeRating';
import VerificationBadge from 'components/VerificationBadge';
import classNames from 'styles/utils/classNames';
import type { HostRequestPlayer } from './types';

interface Props {
  player: HostRequestPlayer;
  /** Avatar diameter; defaults to 40px (the review modal + requests rows). */
  avatarClassName?: string;
  /** Render the name in the invert (white) tone — for the orange requests card. */
  invertText?: boolean;
  /** When set, render this caption (e.g. "Wed • 6 pm - 8 pm") in place of the
   *  skill/rating pills — used by availability requests to show the chosen slot. */
  subLabel?: string;
  /** Optional leading icon rendered before `subLabel` (e.g. a location pin by a city). */
  subLabelIcon?: ReactNode;
  /** Figma's 640px modal sizing from lg up: a 64px avatar and the desktop name style. */
  sizeDesktop?: 'lg';
}

/** Avatar (+ verified shield) + name + rating pills. Shared by the Request review
 *  modal's "From player" card and the requests sub-card rows. */
export default function RequesterInfo({
  player,
  avatarClassName = 'size-10',
  invertText = false,
  subLabel,
  subLabelIcon,
  sizeDesktop,
}: Props) {
  const isLargeAtDesktop = sizeDesktop === 'lg';

  return (
    <div className={classNames('flex items-center gap-ds-sm', isLargeAtDesktop && 'lg:gap-ds-md')}>
      <div
        className={classNames(
          'relative shrink-0',
          avatarClassName,
          isLargeAtDesktop && 'lg:size-16',
        )}
      >
        <img src={player.imageUrl} alt="" className="size-full rounded-full object-cover" />
        <VerificationBadge
          level={player.verificationLevel}
          className={classNames('size-4', isLargeAtDesktop && 'lg:size-[1.6rem]')}
        />
      </div>

      <div className="flex min-w-0 flex-col gap-1">
        <span
          className={classNames(
            'typography-product-card-title-mobile truncate',
            isLargeAtDesktop && 'lg:typography-product-card-title-desktop',
            invertText
              ? 'text-color-text-lightmode-invert'
              : 'text-color-text-lightmode-primary dark:text-color-text-darkmode-primary',
          )}
        >
          {player.name}
        </span>
        {subLabel ? (
          <span
            className={classNames(
              'typography-product-caption flex min-w-0 items-center gap-ds-xs',
              invertText
                ? 'text-color-text-lightmode-invert'
                : 'text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary',
            )}
          >
            {subLabelIcon}
            <span className="truncate">{subLabel}</span>
          </span>
        ) : (
          player.skillLevel && (
            <div className="flex items-center gap-ds-xs">
              <BadgeRating level={player.skillLevel} size="xs" />
              {player.ratingLabel && (
                <BadgeRating level={player.skillLevel} label={player.ratingLabel} size="xs" />
              )}
            </div>
          )
        )}
      </div>
    </div>
  );
}
